import React, { useEffect, useState, useCallback } from 'react';
import { Cookie, X, Settings2, Check } from 'lucide-react';

const STORAGE_KEY = 'lstoit_cookie_consent_v1';

const updateGtagConsent = (granted) => {
  if (typeof window.gtag !== 'function') return;
  window.gtag('consent', 'update', {
    ad_storage: granted ? 'granted' : 'denied',
    ad_user_data: granted ? 'granted' : 'denied',
    ad_personalization: granted ? 'granted' : 'denied',
    analytics_storage: granted ? 'granted' : 'denied',
  });
};

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(true);

  // Show banner if no decision yet
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        setVisible(true);
      } else {
        const parsed = JSON.parse(stored);
        // Re-apply consent on every page load
        updateGtagConsent(parsed.marketing || parsed.analytics);
      }
    } catch (_) {
      setVisible(true);
    }
  }, []);

  // Allow re-opening from elsewhere (e.g. footer link)
  useEffect(() => {
    const handler = () => {
      setShowSettings(true);
      setVisible(true);
    };
    window.addEventListener('open-cookie-settings', handler);
    return () => window.removeEventListener('open-cookie-settings', handler);
  }, []);

  const persist = useCallback((decision) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...decision, ts: Date.now() }));
    } catch (_) {}
  }, []);

  const acceptAll = () => {
    persist({ analytics: true, marketing: true });
    updateGtagConsent(true);
    setVisible(false);
  };

  const rejectAll = () => {
    persist({ analytics: false, marketing: false });
    updateGtagConsent(false);
    setVisible(false);
  };

  const saveSettings = () => {
    persist({ analytics, marketing });
    updateGtagConsent(analytics || marketing);
    setVisible(false);
    setShowSettings(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[200] p-3 sm:p-5 pointer-events-none">
      <div className="max-w-[1100px] mx-auto pointer-events-auto bg-[#0b0b0b] text-white shadow-2xl border-t-2 border-[#e30613] animate-in slide-in-from-bottom duration-300">
        {!showSettings ? (
          /* Compact banner */
          <div className="p-5 sm:p-6 flex flex-col lg:flex-row gap-4 lg:gap-6 items-start lg:items-center">
            <div className="flex items-start gap-3 flex-1">
              <Cookie size={22} className="text-[#e30613] mt-0.5 shrink-0" strokeWidth={1.6} />
              <div>
                <p className="text-[13px] font-bold tracking-[0.18em] mb-2">PLIKI COOKIES</p>
                <p className="text-[12.5px] text-white/75 leading-relaxed max-w-[680px]">
                  Używamy plików cookies, aby zapewnić prawidłowe działanie strony,
                  analizować ruch i mierzyć skuteczność reklam. Pliki marketingowe i analityczne
                  uruchamiamy wyłącznie po Twojej zgodzie. Szczegóły w naszej polityce prywatności.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 sm:gap-3 lg:flex-nowrap lg:shrink-0">
              <button
                onClick={() => setShowSettings(true)}
                className="inline-flex items-center gap-1.5 border border-white/30 hover:border-white/70 text-white/80 hover:text-white text-[10.5px] tracking-[0.18em] font-semibold px-4 py-2.5 transition-colors duration-200"
              >
                <Settings2 size={13} />
                USTAWIENIA
              </button>
              <button
                onClick={rejectAll}
                className="border border-white/30 hover:border-white/70 text-white/80 hover:text-white text-[10.5px] tracking-[0.18em] font-semibold px-4 py-2.5 transition-colors duration-200"
              >
                ODRZUĆ
              </button>
              <button
                onClick={acceptAll}
                className="bg-[#e30613] hover:bg-[#c40510] text-white text-[10.5px] tracking-[0.18em] font-semibold px-5 py-2.5 transition-colors duration-200"
              >
                AKCEPTUJĘ WSZYSTKIE
              </button>
            </div>
          </div>
        ) : (
          /* Settings panel */
          <div className="p-5 sm:p-6">
            <div className="flex items-start justify-between mb-5">
              <div className="flex items-center gap-3">
                <Cookie size={22} className="text-[#e30613]" strokeWidth={1.6} />
                <p className="text-[13px] font-bold tracking-[0.18em]">USTAWIENIA COOKIES</p>
              </div>
              <button
                onClick={() => setShowSettings(false)}
                aria-label="Zamknij"
                className="text-white/60 hover:text-white p-1"
              >
                <X size={18} />
              </button>
            </div>

            <div className="space-y-4 max-w-[820px]">
              {/* Necessary - always on */}
              <div className="border border-white/10 p-4 flex items-start gap-4 opacity-90">
                <div className="mt-1 shrink-0 w-9 h-5 bg-[#1c1c1c] rounded-full relative">
                  <div className="absolute right-0.5 top-0.5 w-4 h-4 bg-[#e30613] rounded-full" />
                </div>
                <div className="flex-1">
                  <p className="text-[12px] font-bold tracking-wide flex items-center gap-2">
                    NIEZBĘDNE
                    <span className="text-[9.5px] tracking-[0.18em] text-white/50 font-normal">(zawsze aktywne)</span>
                  </p>
                  <p className="text-[12px] text-white/70 mt-1.5 leading-relaxed">
                    Pliki konieczne do podstawowego działania strony (sesja, bezpieczeństwo, zapamiętanie zgody).
                    Nie wymagają zgody.
                  </p>
                </div>
              </div>

              {/* Analytics */}
              <label className="border border-white/10 p-4 flex items-start gap-4 cursor-pointer hover:border-white/30 transition-colors duration-200">
                <button
                  type="button"
                  onClick={() => setAnalytics(!analytics)}
                  className={`mt-1 shrink-0 w-9 h-5 rounded-full relative transition-colors duration-200 ${analytics ? 'bg-[#e30613]/30' : 'bg-[#1c1c1c]'}`}
                >
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full transition-all duration-200 ${analytics ? 'right-0.5 bg-[#e30613]' : 'left-0.5 bg-white/40'}`} />
                </button>
                <div className="flex-1">
                  <p className="text-[12px] font-bold tracking-wide">ANALITYCZNE</p>
                  <p className="text-[12px] text-white/70 mt-1.5 leading-relaxed">
                    Pomagają zrozumieć, jak korzystasz ze strony — anonimowe statystyki ruchu i wykorzystania funkcji.
                  </p>
                </div>
              </label>

              {/* Marketing */}
              <label className="border border-white/10 p-4 flex items-start gap-4 cursor-pointer hover:border-white/30 transition-colors duration-200">
                <button
                  type="button"
                  onClick={() => setMarketing(!marketing)}
                  className={`mt-1 shrink-0 w-9 h-5 rounded-full relative transition-colors duration-200 ${marketing ? 'bg-[#e30613]/30' : 'bg-[#1c1c1c]'}`}
                >
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full transition-all duration-200 ${marketing ? 'right-0.5 bg-[#e30613]' : 'left-0.5 bg-white/40'}`} />
                </button>
                <div className="flex-1">
                  <p className="text-[12px] font-bold tracking-wide">MARKETINGOWE</p>
                  <p className="text-[12px] text-white/70 mt-1.5 leading-relaxed">
                    Pozwalają mierzyć skuteczność reklam (Google Ads) i wyświetlać Ci spersonalizowane treści.
                  </p>
                </div>
              </label>
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <button
                onClick={rejectAll}
                className="border border-white/30 hover:border-white/70 text-white/80 hover:text-white text-[10.5px] tracking-[0.18em] font-semibold px-4 py-2.5 transition-colors duration-200"
              >
                ODRZUĆ WSZYSTKIE
              </button>
              <button
                onClick={saveSettings}
                className="inline-flex items-center gap-2 border border-white/40 hover:border-white text-white text-[10.5px] tracking-[0.18em] font-semibold px-5 py-2.5 transition-colors duration-200"
              >
                <Check size={13} />
                ZAPISZ WYBÓR
              </button>
              <button
                onClick={acceptAll}
                className="bg-[#e30613] hover:bg-[#c40510] text-white text-[10.5px] tracking-[0.18em] font-semibold px-5 py-2.5 transition-colors duration-200"
              >
                AKCEPTUJĘ WSZYSTKIE
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieConsent;
